import { Routes } from "#app/routes.ts";
import { FormPage } from "#components/ui/FormPage.tsx";
import { PreventNavigationModal } from "#components/ui/PreventNavigationModal.tsx";
import { useGetSmartReportSuspenseQuery } from "#features/smart-reports/api/useGetSmartReportSuspenseQuery.ts";
import { useUpdateSmartReportMutation } from "#features/smart-reports/api/useUpdateSmartReportMutation.ts";
import { SmartReportCadence } from "#features/smart-reports/components/SmartReportCadence.tsx";
import { SmartReportName } from "#features/smart-reports/components/SmartReportName.tsx";
import { SmartReportUnsubscribed } from "#features/smart-reports/components/SmartReportUnsubscribed.tsx";
import { type UpdateSmartReportForm, useUpdateSmartReportForm } from "#features/smart-reports/hooks/useUpdateSmartReportForm.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { Button, ButtonGroup, Content, Div, Divider, type DivProps, Flex, LinkButton, Text } from "@hopper-ui/components";
import { ArrowLeftIcon } from "@hopper-ui/icons";
import { useCallback, useEffect, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { Navigate, useNavigate } from "react-router";
import { toast } from "react-toastify";

interface Props extends DivProps {
    hasBackButton?: boolean;
}

export const SmartReportForm = (props: Props) => {
    const { data: smartReport } = useGetSmartReportSuspenseQuery();

    if (!smartReport) {
        return <Navigate to={Routes.SmartReports()} replace />;
    }

    return <SmartReportFormContent smartReport={smartReport} {...props} />;
};

const SmartReportFormContent = ({ smartReport, hasBackButton, ...rest }: Props & { smartReport: NonNullable<ReturnType<typeof useGetSmartReportSuspenseQuery>["data"]> }) => {
    const { tShared } = useSharedTranslation();
    const navigate = useNavigate();
    const { trackEvent } = useTracking();
    const { mutate, isPending } = useUpdateSmartReportMutation();

    const defaultValues = useMemo<UpdateSmartReportForm>(() => ({
        cadence: smartReport.cadence,
        isMuted: smartReport.isMuted
    }), [smartReport]);

    const form = useUpdateSmartReportForm(defaultValues);
    const { handleSubmit, reset, formState } = form;

    // Reset the form after the smart report is fetched following a mutation
    useEffect(() => {
        reset(defaultValues);
    }, [smartReport, reset, defaultValues]);

    const onSubmit = useCallback((data: UpdateSmartReportForm) => {
        const cadenceChanged = data.cadence !== smartReport.cadence;
        const unsubscribedChanged = data.isMuted !== smartReport.isMuted;

        mutate(data, {
            onSuccess: () => {
                trackEvent(MixpanelEvent.ButtonClicked, {
                    Trigger: "Save Smart Report Configuration",
                    Cadence: data.cadence,
                    Unsubscribed: String(data.isMuted),
                    "Cadence Changed": String(cadenceChanged),
                    "Unsubscribed Changed": String(unsubscribedChanged),
                    Error: "false"
                });

                toast.success(tShared("smartReports.updateSuccess"));
                navigate(Routes.SmartReports());
            },
            onError: () => {
                trackEvent(MixpanelEvent.ButtonClicked, {
                    Trigger: "Save Smart Report Configuration",
                    Cadence: data.cadence,
                    Unsubscribed: String(data.isMuted),
                    "Cadence Changed": String(cadenceChanged),
                    "Unsubscribed Changed": String(unsubscribedChanged),
                    Error: "true"
                });

                toast.error(tShared("smartReports.updateError"));
            }
        });
    }, [smartReport.cadence, smartReport.isMuted, mutate, trackEvent, tShared, navigate]);

    return (
        <>
            <FormProvider {...form}>
                <FormPage
                    onSubmit={handleSubmit(onSubmit)}
                    content={
                        <Content padding={hasBackButton ? "inset-lg" : 0}>
                            <Flex
                                direction="column"
                                borderRadius="rounded-md"
                                gap="inline-lg"
                                {...rest}
                            >
                                {hasBackButton &&
                                <Div paddingX="inset-lg" paddingTop="inset-lg"><LinkButton
                                    href={Routes.SmartReports()}
                                    variant="ghost-secondary"
                                    size="sm"
                                    alignSelf="start"
                                >
                                    <ArrowLeftIcon size="sm" /> <Text size="sm">{tShared("smartReports.title")}</Text>
                                </LinkButton></Div>}
                                <SmartReportName />
                                <Divider />
                                <SmartReportCadence dayOfWeek={smartReport.dayOfWeek} />
                                <Divider />
                                <SmartReportUnsubscribed />
                            </Flex>
                        </Content>
                    }
                    footer={
                        formState.isDirty &&
                            <ButtonGroup isDisabled={isPending}>
                                <Button variant="secondary" onPress={() => reset()}>
                                    {tShared("smartReports.discard")}
                                </Button>
                                <Button type="submit" isLoading={isPending}>
                                    {tShared("smartReports.submit")}
                                </Button>
                            </ButtonGroup>
                    }
                />
            </FormProvider>
            <PreventNavigationModal isDisabled={!formState.isDirty || isPending} onDiscard={reset} />
        </>
    );
};
